<script lang="ts">
  // BJL-CHECK: Input, SearchFilter, Dropdown — the field is a bare input inside a labelled
  // row (bjl Input wraps a FieldWrapper this two-line form has no room for), and the
  // suggestion list merges three sources (current location, station complexes with route
  // bullets, geocoded addresses) with pointerdown-to-commit so a tap lands before blur.
  // See artifacts/deviations/PlanPage.md.
  import { InputBare } from '@byjackli-labs/ui/Input'
  import { api, type GeocodeResult } from '../../services/api'
  import { Colors } from '../../ui/colors'
  import { searchStations, stationMatchScore } from './stationSearch'
  import { Type } from '../../ui/theme'
  import { useStopsStore } from '../../store/stops'
  import type { PlanPlace } from '../../store/tripPlan'
  import { storeSelect } from '../../lib/store.svelte'
  import RouteBadge from '../../components/RouteBadge.svelte'

  interface Suggestion {
    place: PlanPlace
    detail?: string
    routes?: string[]
  }

  interface Props {
    label: string
    place: PlanPlace | null
    onSelect: (p: PlanPlace | null) => void
    currentLocation: { latitude: number; longitude: number } | null
  }

  const { label, place, onSelect, currentLocation }: Props = $props()

  const stops = storeSelect(useStopsStore, (s) => s.stops)

  let text = $state('')
  let focused = $state(false)
  let addresses = $state<GeocodeResult[]>([])
  let seq = 0

  // Keep the visible text in sync when the place changes from outside (swap, recents).
  // This also seeds the field on mount, which is why `text` starts empty rather than
  // reading `place` at initialisation.
  $effect(() => {
    text = place?.name ?? ''
  })

  // Address autocomplete, debounced. Stale responses are dropped by sequence number.
  $effect(() => {
    const query = text.trim()
    const isFocused = focused
    const picked = place
    if (!isFocused || query.length < 3 || picked) {
      addresses = []
      return
    }
    const timer = setTimeout(async () => {
      const mine = ++seq
      try {
        const { results } = await api.geocode(query)
        if (mine === seq) addresses = results
      } catch {
        /* autocomplete stays quiet on failure */
      }
    }, 300)
    return () => clearTimeout(timer)
  })

  const suggestions = $derived.by<Suggestion[]>(() => {
    if (!focused || place) return []
    const out: Suggestion[] = []
    const query = text.trim().toLowerCase()

    if (currentLocation && query.length === 0) {
      out.push({
        place: {
          kind: 'current',
          name: 'Current location',
          lat: currentLocation.latitude,
          lon: currentLocation.longitude,
        },
      })
    }
    // Stations first, and they outrank the geocoder: a rider typing "World Trade Center"
    // into a trip planner means the station, even though MTA files it under
    // `Chambers St/WTC/Park Place/Cortlandt St` and the address is the only literal match.
    // `searchStations` is what closes that gap — see stationSearch.ts.
    const stations = query.length >= 1 ? searchStations(stops.value, query) : []
    for (const st of stations) {
      out.push({
        place: { kind: 'station', id: st.id, name: st.name, lat: st.lat, lon: st.lon },
        routes: st.routes,
      })
    }
    // An address restating a station already listed above is the same pin twice, and the
    // duplicate reads as if the address were the real answer. Score 0 is "this name IS that
    // station", spelled-out abbreviations included — the geocoder's "World Trade Center"
    // against the WTC complex, which is the whole case this field was getting wrong.
    let shown = 0
    for (const a of addresses) {
      if (shown >= 4) break
      const name = a.name || a.label
      if (stations.some((st) => stationMatchScore(st.name, name) === 0)) continue
      out.push({
        place: { kind: 'address', name, lat: a.lat, lon: a.lon },
        detail: a.borough ? `${a.borough} · address` : 'address',
      })
      shown += 1
    }
    return out
  })
</script>

<div style="position: relative; flex: 1; min-width: 0;">
  <div style="display: flex; align-items: center; gap: 8px;">
    <span
      style="color: {Colors.textMuted}; {Type.caption}; font-weight: 700; width: 34px; flex-shrink: 0;"
    >{label}</span>
    <InputBare
      bind:value={text}
      placeholder="Station or address"
      aria-label={label}
      onfocus={() => { focused = true }}
      onblur={() => setTimeout(() => { focused = false }, 150)}
      oninput={() => {
        // Editing invalidates the previous pick.
        if (place) onSelect(null)
      }}
      style="flex: 1; min-width: 0;{place ? ` border-color: ${Colors.borderStrong};` : ''}"
    />
  </div>

  {#if suggestions.length > 0}
    <div
      style="
        position: absolute; top: 100%; left: 42px; right: 0; margin-top: 4px;
        background-color: {Colors.surfaceElevated};
        border: 1px solid {Colors.borderStrong}; border-radius: var(--bjl-radius-md, 10px);
        box-shadow: var(--bjl-shadow-lg, 0 8px 24px rgba(0,0,0,0.55));
        z-index: 50; overflow: hidden;
        max-height: 260px; overflow-y: auto;
      "
    >
      {#each suggestions as s, i (`${s.place.kind}:${s.place.id ?? s.place.name}:${i}`)}
        <button
          onpointerdown={(e) => {
            e.preventDefault()
            onSelect(s.place)
          }}
          style="
            display: flex; align-items: center; gap: 8px; width: 100%;
            padding: 9px 10px; border: none; cursor: pointer; text-align: left;
            background-color: transparent;
            border-bottom: {i < suggestions.length - 1 ? `1px solid ${Colors.border}` : 'none'};
          "
        >
          <span
            style="
              color: {Colors.text}; {Type.body}; font-weight: 600;
              overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            "
          >{s.place.name}</span>
          {#if s.routes}
            <span style="display: flex; gap: 3px; margin-left: auto; flex-shrink: 0;">
              {#each s.routes.slice(0, 5) as r (r)}
                <RouteBadge routeId={r} size={16} />
              {/each}
            </span>
          {/if}
          {#if s.detail}
            <span style="margin-left: auto; color: {Colors.textMuted}; {Type.caption}; flex-shrink: 0;">
              {s.detail}
            </span>
          {/if}
        </button>
      {/each}
    </div>
  {/if}
</div>
